import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHelmet from '../../components/SEOHelmet';
import { BreadcrumbSchema, TripSchema } from '../../components/SchemaMarkup';
import { track } from '../../utils/analytics';
import { routes } from '../../data/routes';
import { destinations } from '../../data/destinations';
import {
  FaArrowLeft, FaPlane, FaShip, FaTrain, FaCar,
  FaRegClock, FaRegLightbulb, FaExclamationTriangle, FaRegMoon,
} from 'react-icons/fa';
import './RouteMap.css';

// Longest leg wins, so "road then flight" reads as a flight.
const legIcon = (mode = '') => {
  if (/flight|fly/i.test(mode)) return FaPlane;
  if (/ferry|boat/i.test(mode)) return FaShip;
  if (/train|metro|mrt|monorail|rail/i.test(mode)) return FaTrain;
  return FaCar;
};

const RouteMap = () => {
  const { country } = useParams();
  const route = routes[country];
  const atlas = destinations.find((d) => d.id === country);

  useEffect(() => { window.scrollTo(0, 0); }, [country]);

  if (!route) {
    return (
      <div className="route-missing">
        <div className="container">
          <h1>No route for that destination yet</h1>
          <p>We only map routes for countries we have travelled end to end ourselves.</p>
          <Link to="/places" className="btn btn-primary">Browse destinations</Link>
        </div>
      </div>
    );
  }

  const name = route.name;
  const short = (atlas?.name || name).split(',')[0];
  const stayed = route.stops.filter((s) => s.nights > 0).length;

  return (
    <div className="route-page">
      <SEOHelmet
        title={`${short} Itinerary — A ${route.nights}-Night Route That Works`}
        description={`A suggested ${short} route: ${route.stops.map((s) => s.name).join(' → ')}. How long to stay in each place, how to get between them, and the mistake that costs people days.`}
        keywords={`${short} itinerary, ${short} route, ${route.nights} days in ${short}, how many days in ${short}`}
        url={`https://safarnamah.in/routes/${country}`}
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://safarnamah.in' },
        { name: 'Destinations', url: 'https://safarnamah.in/places' },
        { name: short, url: `https://safarnamah.in/places/${country}` },
        { name: 'Suggested route', url: `https://safarnamah.in/routes/${country}` },
      ]} />
      <TripSchema
        name={`${route.nights}-night ${short} route`}
        description={route.lead}
        url={`https://safarnamah.in/routes/${country}`}
        stops={route.stops}
      />

      <div className="container route-in">
        <Link to={`/places/${country}`} className="back-link">
          <FaArrowLeft aria-hidden="true" /> {short} guide
        </Link>

        <header className="route-head">
          <span className="kicker">Suggested route</span>
          <h1>How to travel {name}</h1>
          <p className="route-lead">{route.lead}</p>

          <dl className="route-facts">
            <div>
              <dt>Route</dt>
              <dd>{route.shape}</dd>
            </div>
            <div>
              <dt>Length</dt>
              <dd>{route.nights} nights, {stayed} {stayed === 1 ? 'base' : 'bases'}</dd>
            </div>
            {atlas?.bestTime && (
              <div>
                <dt>Best months</dt>
                <dd>{atlas.bestTime}</dd>
              </div>
            )}
          </dl>
        </header>

        <ol className="route-line">
          {route.stops.map((stop, i) => {
            const LegIcon = stop.onward ? legIcon(stop.onward.mode) : null;
            return (
              // No entry animation here on purpose. The route is the whole point of
              // the page, and anything that starts at zero opacity stays invisible
              // if the animation frame never runs — background tab, throttled JS, or
              // a page opened part-scrolled.
              <li className="route-stop" key={stop.name}>
                <div className="route-marker" aria-hidden="true">{i + 1}</div>

                <div className="route-body">
                  <div className="route-stop-head">
                    <h2>{stop.name}</h2>
                    {stop.nights > 0 && (
                      <span className="route-nights">
                        <FaRegMoon aria-hidden="true" />
                        {stop.nights} {stop.nights === 1 ? 'night' : 'nights'}
                      </span>
                    )}
                  </div>
                  <p>{stop.note}</p>

                  {stop.onward && (
                    <div className="route-leg">
                      <LegIcon className="route-leg-icon" aria-hidden="true" />
                      <span className="route-leg-mode">{stop.onward.mode}</span>
                      <span className="route-leg-time">
                        <FaRegClock aria-hidden="true" /> {stop.onward.time}
                      </span>
                      <span className="route-leg-detail">{stop.onward.detail}</span>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        <div className="route-notes">
          <section className="route-note">
            <h2><FaRegClock aria-hidden="true" /> If you have less time</h2>
            <p>{route.shorter}</p>
          </section>
          <section className="route-note">
            <h2><FaRegLightbulb aria-hidden="true" /> If you have more</h2>
            <p>{route.longer}</p>
          </section>
        </div>

        <section className="route-mistake">
          <h2><FaExclamationTriangle aria-hidden="true" /> The mistake to avoid</h2>
          <p>{route.mistake}</p>
        </section>

        <aside className="route-foot">
          <h2>Next steps</h2>
          <p>
            This route is free to copy, adapt or ignore. If you would rather not
            build it yourself, tell us your dates and we will shape it around them.
          </p>
          <div className="route-foot-links">
            <Link to={`/checklist/${country}`} className="btn btn-secondary">
              {short} packing list
            </Link>
            <Link to={`/places/${country}`} className="btn btn-secondary">
              Read the {short} guide
            </Link>
            <Link
              to="/plan-trip"
              className="btn btn-primary"
              onClick={() => track('plan_trip_click', { source: 'route', destination: country })}
            >
              Plan this trip with us
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default RouteMap;
